import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, Upload, Copy, ExternalLink, Eye, Music, Heart, Download, X, Loader2, Link2, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { getImageUrl } from '../../utils/api';
import './AdminStyles.css';
import './PromoManagement.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PromoManagement = () => {
  const [promos, setPromos] = useState([]);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [uploading, setUploading] = useState({});
  const [formData, setFormData] = useState({
    title: '', artist_id: '', artist_name: '', genre: '', sub_genre: '',
    description: '', release_date: '', is_pre_release: false, expires_at: '',
    webhook_url: '', status: 'draft',
    streaming_links: { spotify: '', apple_music: '', soundcloud: '', beatport: '', youtube: '', deezer: '' }
  });

  const getToken = () => localStorage.getItem('adminToken');

  const fetchData = useCallback(async () => {
    const token = getToken();
    try {
      const [promosRes, artistsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/promos`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_URL}/api/artists`)
      ]);
      if (promosRes.ok) setPromos(await promosRes.json());
      if (artistsRes.ok) setArtists(await artistsRes.json());
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setFormData({
      title: '', artist_id: '', artist_name: '', genre: '', sub_genre: '',
      description: '', release_date: '', is_pre_release: false, expires_at: '',
      webhook_url: '', status: 'draft',
      streaming_links: { spotify: '', apple_music: '', soundcloud: '', beatport: '', youtube: '', deezer: '' }
    });
    setEditingPromo(null);
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        title: promo.title || '',
        artist_id: promo.artist_id || '',
        artist_name: promo.artist_name || '',
        genre: promo.genre || '',
        sub_genre: promo.sub_genre || '',
        description: promo.description || '',
        release_date: promo.release_date || '',
        is_pre_release: promo.is_pre_release || false,
        expires_at: promo.expires_at || '',
        webhook_url: promo.webhook_url || '',
        status: promo.status || 'draft',
        streaming_links: {
          spotify: promo.streaming_links?.spotify || '',
          apple_music: promo.streaming_links?.apple_music || '',
          soundcloud: promo.streaming_links?.soundcloud || '',
          beatport: promo.streaming_links?.beatport || '',
          youtube: promo.streaming_links?.youtube || '',
          deezer: promo.streaming_links?.deezer || '',
        }
      });
    } else {
      resetForm();
    }
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const handleArtistSelect = (artistId) => {
    const artist = artists.find(a => a.id === artistId);
    setFormData(prev => ({ ...prev, artist_id: artistId, artist_name: artist?.name || '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();
    const links = Object.fromEntries(Object.entries(formData.streaming_links).filter(([, v]) => v));
    const payload = { ...formData, streaming_links: links };

    try {
      const url = editingPromo
        ? `${API_URL}/api/admin/promos/${editingPromo.id}`
        : `${API_URL}/api/admin/promos`;
      const res = await fetch(url, {
        method: editingPromo ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to save promo');
      toast.success(editingPromo ? 'Promo updated' : 'Promo created');
      closeModal();
      fetchData();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (promoId) => {
    if (!window.confirm('Delete this promo page?')) return;
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/promos/${promoId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('Promo deleted');
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const handleFileUpload = async (promoId, file, fileType) => {
    const token = getToken();
    setUploading(prev => ({ ...prev, [`${promoId}-${fileType}`]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/admin/promos/${promoId}/upload/${fileType}`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      toast.success(`${fileType.replace('_', ' ')} uploaded`);
      fetchData();
    } catch (err) { toast.error(err.message); }
    finally { setUploading(prev => ({ ...prev, [`${promoId}-${fileType}`]: false })); }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/promo/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Promo link copied to clipboard');
  };

  const handlePromote = async (promoId, title) => {
    if (!window.confirm(`Promote "${title}" to Releases?\n\nThis will create a new Release and archive the promo.`)) return;
    const token = getToken();
    try {
      const res = await fetch(`${API_URL}/api/admin/promos/${promoId}/promote`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to promote');
      const data = await res.json();
      toast.success(data.message);
      fetchData();
    } catch (err) { toast.error(err.message); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('URL copied');
  };

  if (loading) return <div className="loading">Loading promos...</div>;

  return (
    <div className="promo-mgmt management-page" data-testid="promo-management">
      <div className="page-header">
        <h1>Promo Pages</h1>
        <button className="btn-primary" onClick={() => openModal()} data-testid="create-promo-btn">
          <Plus size={16} /> New Promo
        </button>
      </div>

      {promos.length === 0 ? (
        <div className="promo-mgmt__empty" data-testid="promo-empty-state">
          <Music size={48} />
          <h3>No promo pages yet</h3>
          <p>Create promo landing pages for your releases to share with DJs and industry contacts.</p>
        </div>
      ) : (
        <div className="promo-mgmt__grid" data-testid="promo-list">
          {promos.map(promo => (
            <div key={promo.id} className="promo-mgmt__card" data-testid={`promo-card-${promo.id}`}>
              <div className="promo-mgmt__card-art">
                {promo.cover_art ? (
                  <img src={getImageUrl(promo.cover_art)} alt={promo.title} />
                ) : (
                  <div className="promo-mgmt__card-art-placeholder"><Music size={32} /></div>
                )}
                <span className={`promo-mgmt__status promo-mgmt__status--${promo.status}`}>
                  {promo.status}
                </span>
              </div>
              <div className="promo-mgmt__card-body">
                <h3>{promo.title}</h3>
                <p className="promo-mgmt__artist">{promo.artist_name}</p>
                {promo.genre && <p className="promo-mgmt__genre">{promo.genre}{promo.sub_genre ? ` / ${promo.sub_genre}` : ''}</p>}
                {promo.expires_at && <p className="promo-mgmt__expires">Expires: {promo.expires_at}</p>}

                <div className="promo-mgmt__stats">
                  <span><Eye size={13} /> {promo.play_count || 0}</span>
                  <span><Heart size={13} /> {promo.like_count || 0}</span>
                  <span><Download size={13} /> {promo.download_count || 0}</span>
                </div>

                {/* File uploads */}
                <div className="promo-mgmt__files">
                  {['cover_art', 'mp3', 'wav'].map(ft => {
                    const hasFile = ft === 'cover_art' ? promo.cover_art : promo[`${ft}_url`];
                    const isUploading = uploading[`${promo.id}-${ft}`];
                    const accept = ft === 'cover_art' ? 'image/*' : ft === 'mp3' ? '.mp3,audio/mpeg' : '.wav,audio/wav';
                    const label = ft === 'cover_art' ? 'Cover Art' : ft.toUpperCase();
                    return (
                      <label key={ft} className={`promo-mgmt__file-btn ${hasFile ? 'uploaded' : ''}`}
                        data-testid={`upload-${ft}-${promo.id}`}>
                        {isUploading ? <Loader2 size={13} className="spin" /> : <Upload size={13} />}
                        {label} {hasFile ? '(Replace)' : ''}
                        <input type="file" accept={accept} hidden
                          onChange={e => e.target.files[0] && handleFileUpload(promo.id, e.target.files[0], ft)} />
                      </label>
                    );
                  })}
                </div>

                {/* File URLs */}
                {(promo.mp3_url || promo.wav_url) && (
                  <div className="promo-mgmt__urls" data-testid={`file-urls-${promo.id}`}>
                    {promo.mp3_url && (
                      <button className="promo-mgmt__url-btn" onClick={() => copyUrl(promo.mp3_url)} title={promo.mp3_url} data-testid={`mp3-url-${promo.id}`}>
                        <Link2 size={12} /> MP3 URL
                      </button>
                    )}
                    {promo.wav_url && (
                      <button className="promo-mgmt__url-btn" onClick={() => copyUrl(promo.wav_url)} title={promo.wav_url} data-testid={`wav-url-${promo.id}`}>
                        <Link2 size={12} /> WAV URL
                      </button>
                    )}
                  </div>
                )}

                <div className="promo-mgmt__actions">
                  <button onClick={() => openModal(promo)} title="Edit" data-testid={`edit-promo-${promo.id}`}><Edit size={15} /></button>
                  <button onClick={() => copyLink(promo.slug)} title="Copy link" data-testid={`copy-link-${promo.id}`}><Copy size={15} /></button>
                  {promo.status === 'published' && (
                    <a href={`/promo/${promo.slug}`} target="_blank" rel="noreferrer" title="View" data-testid={`view-promo-${promo.id}`}><ExternalLink size={15} /></a>
                  )}
                  {promo.status === 'published' && (
                    <button onClick={() => handlePromote(promo.id, promo.title)} title="Promote to Release" className="promote" data-testid={`promote-promo-${promo.id}`}><ArrowUpRight size={15} /></button>
                  )}
                  <button onClick={() => handleDelete(promo.id)} className="danger" title="Delete" data-testid={`delete-promo-${promo.id}`}><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content promo-mgmt__modal" onClick={e => e.stopPropagation()} data-testid="promo-modal">
            <div className="modal-header">
              <h2>{editingPromo ? 'Edit Promo' : 'New Promo Page'}</h2>
              <button className="close-btn" onClick={closeModal}><X size={20} /></button>
            </div>
            <form className="modal-form" onSubmit={handleSubmit} data-testid="promo-form">
              <div className="form-group">
                <label>Title *</label>
                <input value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })}
                  required placeholder="e.g. Eclipse (Club Mix)" data-testid="promo-title-input" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Artist</label>
                  <select value={formData.artist_id} onChange={e => handleArtistSelect(e.target.value)} data-testid="promo-artist-select">
                    <option value="">Select artist...</option>
                    {artists.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Artist Name (override)</label>
                  <input value={formData.artist_name} onChange={e => setFormData({ ...formData, artist_name: e.target.value })}
                    placeholder="Or type manually" data-testid="promo-artist-name-input" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Genre</label>
                  <input value={formData.genre} onChange={e => setFormData({ ...formData, genre: e.target.value })}
                    placeholder="e.g. Melodic House" data-testid="promo-genre-input" />
                </div>
                <div className="form-group">
                  <label>Sub-Genre</label>
                  <input value={formData.sub_genre} onChange={e => setFormData({ ...formData, sub_genre: e.target.value })}
                    placeholder="e.g. Techno" data-testid="promo-subgenre-input" />
                </div>
              </div>

              <div className="form-group">
                <label>Release Notes / Description</label>
                <textarea rows={4} value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Write release notes, promo copy, or a message to DJs..." data-testid="promo-description-input" />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Release Date</label>
                  <input type="date" value={formData.release_date} onChange={e => setFormData({ ...formData, release_date: e.target.value })}
                    data-testid="promo-date-input" />
                </div>
                <div className="form-group">
                  <label>Status</label>
                  <select value={formData.status} onChange={e => setFormData({ ...formData, status: e.target.value })}
                    data-testid="promo-status-select">
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="checkbox-label">
                    <input type="checkbox" checked={formData.is_pre_release}
                      onChange={e => setFormData({ ...formData, is_pre_release: e.target.checked })}
                      data-testid="promo-prerelease-check" />
                    Pre-release
                  </label>
                </div>
                <div className="form-group">
                  <label>Expiry Date</label>
                  <input type="date" value={formData.expires_at} onChange={e => setFormData({ ...formData, expires_at: e.target.value })}
                    data-testid="promo-expiry-input" />
                  <small className="promo-mgmt__hint">Page becomes unavailable after this date</small>
                </div>
              </div>

              <div className="form-group">
                <label>Campaign Tracking URL (Heyday / Webhook)</label>
                <input value={formData.webhook_url} onChange={e => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://heyday-production.up.railway.app" data-testid="promo-webhook-input" />
                <small className="promo-mgmt__hint">Heyday: paste base URL — events fire with the ?ref= token from the email link. Also used for backend webhook.</small>
              </div>

              <fieldset className="promo-mgmt__links-fieldset">
                <legend>Streaming Links (optional)</legend>
                {['spotify', 'apple_music', 'soundcloud', 'beatport', 'youtube', 'deezer'].map(platform => (
                  <div className="form-group" key={platform}>
                    <label>{platform.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</label>
                    <input value={formData.streaming_links[platform] || ''}
                      onChange={e => setFormData(prev => ({
                        ...prev, streaming_links: { ...prev.streaming_links, [platform]: e.target.value }
                      }))}
                      placeholder={`https://${platform.replace('_', '')}.com/...`}
                      data-testid={`promo-link-${platform}`} />
                  </div>
                ))}
              </fieldset>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn-primary" data-testid="promo-save-btn">
                  {editingPromo ? 'Update Promo' : 'Create Promo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromoManagement;
